import { useTranslation } from 'react-i18next';

import { DesignSection } from '../enums';

/**
 * Converts a translation path to camelCase format.
 *
 * @param input - The translation path string with dot notation (e.g., 'APPLY_SAME_EDGES', 'OPTIONS.BROWSE_SINKS')
 * @returns The camelCase version of the input (e.g., 'applySameEdges', 'optionsBrowseSinks')
 *
 * @example
 * convertTranslationPathToCamelCase('APPLY_SAME_EDGES') // returns 'applySameEdges'
 * convertTranslationPathToCamelCase('OPTIONS.INFO.BROWSE_SINKS') // returns 'optionsInfoBrowseSinks'
 */
const convertTranslationPathToCamelCase = (input: string): string => {
  const parts = input.split('.');
  let isFirstWord = true;

  return parts
    .map((part) => {
      const words = part.split('_');

      return words
        .map((word) => {
          if (isFirstWord) {
            isFirstWord = false;

            return word.charAt(0).toLowerCase() + word.slice(1).toLowerCase();
          }

          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join('');
    })
    .join('');
};

interface UseGetDesignSectionTranslationsReturn {
  title: string;
  description: string;
  [key: string]: string;
}

/**
 * Custom hook to retrieve translations for a design section.
 *
 * Retrieves the title and description translations for a given design section,
 * along with any additional translation keys specified. Additional keys are
 * automatically converted to camelCase for convenient destructuring.
 *
 * @param designSection - The design section to get translations for
 * @param otherKeys - Optional array of additional translation keys to retrieve.
 *                    Keys should be relative to `DESIGN_SIDE_PANEL.{designSection}.`
 *                    and will be converted to camelCase in the return object.
 *
 * @returns An object containing title, description, and any additional translations
 *          with camelCase property names
 *
 * @example
 * // Get only title and description
 * const { title, description } = useGetDesignSectionTranslations(DesignSection.EDGES);
 *
 * @example
 * // Get title, description, and one additional key
 * const { title, description, applySameEdges } = useGetDesignSectionTranslations(
 *   DesignSection.EDGES,
 *   ['APPLY_SAME_EDGES']
 * );
 *
 * @example
 * // Get multiple additional keys with nested paths
 * const { title, description, optionsInfoBrowseSinks, optionsBrowseSinks, customerSuppliedFree } =
 *   useGetDesignSectionTranslations(DesignSection.SINKS, [
 *     'OPTIONS.INFO.BROWSE_SINKS',
 *     'OPTIONS.BROWSE_SINKS',
 *     'CUSTOMER_SUPPLIED_FREE'
 *   ]);
 */
export const useGetDesignSectionTranslations = (
  designSection: Omit<DesignSection, DesignSection.SHAPES>,
  otherKeys: string[] = []
): UseGetDesignSectionTranslationsReturn => {
  const { t } = useTranslation();

  const title = t(`DESIGN_SIDE_PANEL.${designSection}.TITLE`);
  const description = t(`DESIGN_SIDE_PANEL.${designSection}.DESCRIPTION`);

  const otherTranslations = otherKeys.reduce(
    (acc, key) => {
      const camelCaseKey = convertTranslationPathToCamelCase(key);
      const fullKey = `DESIGN_SIDE_PANEL.${designSection}.${key}`;

      acc[camelCaseKey] = t(fullKey);

      return acc;
    },
    {} as Record<string, string>
  );

  return { title, description, ...otherTranslations };
};
